import { useSetFooterIsVisible, useSetIntercomIsVisible } from '@/queries/layout.queries';
import { useDisclosure } from '@cmpsr/components';
import { useEffect, useMemo } from 'react';
import { mapCTA } from './components/CallToActions';
import { ImageInterstitialType } from './components/SurveyItem/components/ImageInterstitial';
import { normalizeQuestionnaire } from './DecisionTree.normalizer';
import { useHandleAnswers, usePagination } from './hooks';
import { NextSurveyItemType, OnItemViewedCB, Steps, UserQuestionnaireType, UseSetupCallbackCB } from './types';

type DecisionTreeControllerProps = {
  userQuestionnaire: UserQuestionnaireType;
  callback: UseSetupCallbackCB;
  onItemViewed?: OnItemViewedCB;
  editQuestion?: NextSurveyItemType;
  backOnFirstSurveyItem?: () => void;
  isUnclearQuestionsFlow?: boolean;
};

export default function useDecisionTreeController({
  userQuestionnaire,
  callback,
  onItemViewed,
  editQuestion,
  backOnFirstSurveyItem,
  isUnclearQuestionsFlow = false,
}: DecisionTreeControllerProps) {
  const mutateIntercomIsVisible = useSetIntercomIsVisible();
  const { questionnaire: normalizedQuestionnaire, unclearAnswersFlow } = useMemo(
    () => normalizeQuestionnaire(userQuestionnaire, isUnclearQuestionsFlow),
    [isUnclearQuestionsFlow, userQuestionnaire]
  );
  const isTestResultsQuestionnaire = normalizedQuestionnaire.type === 'screening';
  const steps: Steps = normalizedQuestionnaire.sections.map(({ id, name }) => ({ id, name }));
  const initialState = editQuestion
    ? editQuestion
    : isUnclearQuestionsFlow
    ? userQuestionnaire.nextIDontKnowAnsweredQuestion
    : userQuestionnaire.nextSurveyItem;

  const {
    state: answerState,
    answersDispatch,
    submitAnswer,
    submitIDKAnswer,
    summary,
    result,
  } = useHandleAnswers({ callback, answers: userQuestionnaire.answers, initialState });
  const {
    currentPage: { currentSurveyItem, currentSection },
    paginationDispatch,
    activeStep,
    goToNextSurveyItem,
    showBackButton,
    isLastSurveyItem,
  } = usePagination({
    steps,
    initialState,
    editQuestion,
    answersDispatch,
    submitAnswer,
    backOnFirstSurveyItem,
    isUnclearQuestionsFlow,
    unclearAnswersFlow,
    isTestResultsQuestionnaire,
    onItemViewed,
  });

  // Use the isLastSurveyItem from userQuestionnaire if available
  // This ensures the correct state when navigating back via URL
  const isCurrentItemLast = isLastSurveyItem || 
    (userQuestionnaire.isLastSurveyItem && userQuestionnaire.nextSurveyItem?.surveyItemId === currentSurveyItem);

  useEffect(() => {
    //This useEffect is used to prefetch the next image interstitial
    //to make a smoother transition when the user navigates to the next item
    if (!currentSurveyItem || !normalizedQuestionnaire) return;

    const allItems = normalizedQuestionnaire.sections.flatMap((s) => s.surveyItems);
    const currentItemIndex = allItems.findIndex((item) => item.id === currentSurveyItem);

    if (currentItemIndex === -1 || currentItemIndex >= allItems.length - 1) {
      return;
    }

    const nextItem = allItems[currentItemIndex + 1];

    if (nextItem?.surveyItemType === 'interstitial' && nextItem?.type === 'imageInterstitial') {
      const imageId = (nextItem as ImageInterstitialType).resource;
      if (imageId) {
        const preloadUrl = `/images/interstitials/${imageId}/${imageId}-800w.avif`;
        const img = new Image();
        img.src = preloadUrl;
      }
    }
  }, [currentSurveyItem, normalizedQuestionnaire]);

  const mutateFooterIsVisible = useSetFooterIsVisible();

  const cta: keyof typeof mapCTA =
    editQuestion !== undefined
      ? 'save'
      : isCurrentItemLast
      ? 'done'
      : currentSurveyItem !== 'result' || answerState.answer?.type !== 'result'
      ? 'next'
      : !answerState?.answer?.followUp
      ? 'back'
      : 'close';

  useEffect(() => {
    mutateIntercomIsVisible.mutateAsync(false);
    return () => {
      mutateIntercomIsVisible.mutateAsync(true);
    };
  }, []);

  useEffect(() => {
    mutateFooterIsVisible.mutateAsync(false);
    return () => {
      mutateFooterIsVisible.mutateAsync(true);
    };
  }, []);

  const whyWeAskModalDisclosure = useDisclosure();
  const handleNextClick = async () => {
    whyWeAskModalDisclosure.onClose();
    await goToNextSurveyItem();
    // Scroll to the top when navigating to a new survey item
    window.scrollTo(0, 0);
  };

  // Auto-navigate when the complete flag is set to true
  useEffect(() => {
    if (answerState.complete && answerState.isAnswered) {
      handleNextClick();
    }
  }, [answerState.complete, answerState.isAnswered]);

  return {
    normalizedQuestionnaire,
    currentSurveyItem,
    currentSection,
    paginationDispatch,
    activeStep,
    goToNextSurveyItem: handleNextClick,
    showBackButton,
    cta,
    answerState,
    answersDispatch,
    submitIDKAnswer,
    summary,
    result,
    steps,
    whyWeAskModalDisclosure,
  };
}
